import React from 'react';
import { Menu, MenuItem, ListItemIcon, ListItemText, useTheme, alpha } from '@mui/material';
import { Plus, Edit, Trash2, FileText } from 'lucide-react';

interface ProjectHierarchyContextMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onAddSubfolder: () => void;
  onCreateProject: () => void;
  onEdit: () => void;
  onMove: () => void;
  onDelete: () => void;
  nodeName?: string;
  hasChildren?: boolean;
  hasProjects?: boolean;
}

export const ProjectHierarchyContextMenu: React.FC<ProjectHierarchyContextMenuProps> = ({
  anchorEl,
  open,
  onClose,
  onAddSubfolder,
  onCreateProject,
  onEdit,
  onMove,
  onDelete,
  nodeName,
  hasChildren = false,
  hasProjects = false,
}) => {
  const theme = useTheme();

  // Check if delete should be disabled
  const canDelete = !hasChildren && !hasProjects;

  const menuItems = [
    {
      icon: <Plus size={16} />,
      text: 'Add Subfolder',
      onClick: onAddSubfolder,
      color: theme.palette.text.primary,
    },
    {
      icon: <FileText size={16} />,
      text: 'Create Project',
      onClick: onCreateProject,
      color: theme.palette.primary.main,
    },
    {
      icon: <Edit size={16} />,
      text: 'Edit',
      onClick: onEdit,
      color: theme.palette.text.primary,
    },
    {
      icon: <Trash2 size={16} />,
      text: canDelete ? 'Delete' : 'Delete (has sub-items)',
      onClick: canDelete ? onDelete : () => {},
      color: canDelete ? theme.palette.error.main : theme.palette.text.disabled,
      disabled: !canDelete,
    },
  ];

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      PaperProps={{
        sx: {
          bgcolor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.shadows[8],
          minWidth: 160,
        },
      }}
    >
      {menuItems.map((item, index) => (
        <MenuItem
          key={index}
          onClick={() => {
            if (!item.disabled) {
              item.onClick();
              onClose();
            }
          }}
          disabled={item.disabled}
          sx={{
            '&:hover': {
              backgroundColor: item.disabled
                ? 'transparent'
                : alpha(theme.palette.primary.main, 0.08),
            },
            '&.Mui-disabled': {
              opacity: 0.6,
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 32, color: item.color }}>{item.icon}</ListItemIcon>
          <ListItemText
            primary={item.text}
            sx={{
              '& .MuiListItemText-primary': {
                color: item.color,
                fontSize: '0.875rem',
              },
            }}
          />
        </MenuItem>
      ))}
    </Menu>
  );
};
